<template>
  <WizardModal
    v-model="currentStep"
    v-model:show-modal="isVisible"
    :steps="wizardSteps"
    :modal-title="t('Adicionar compromisso na agenda')"
      size="compactDesktop"
    :show-pre-wizard="showingOrcamentoSelection"
    :show-summary="!showingOrcamentoSelection"
    :summary-items="summaryItems"
    custom-buttons
    :next-label="nextButtonLabel"
    :next-icon="nextButtonIcon"
    @finish="createCompromisso"
    @cancel="handleCancel"
    @close="handleModalClose"
  >
    <!-- Pre-wizard: Orcamento Selection -->
    <template #pre-wizard>
      <div>
        <!-- Date/Time Info -->
        <div class="bloqueio-button-container">
          <Button
            data-testid="wizard-bloqueio-btn"
            :label="t('Novo bloqueio de agenda')"
            icon="pi pi-ban"
            severity="warn"
            @click="selectBloqueio"
          />
        </div>

        <h4><b>{{ t('Data') }}:</b> {{ formatDateOnly(selectedDateTime.start) }}</h4>
        <h4><b>{{ t('Hora') }}:</b> {{ formatTime(selectedDateTime.start) }}</h4>
        <p>{{ t('Clique no orçamento abaixo para incluir na agenda') }}.</p>

        <!-- Search Input -->
        <input-text
          v-model="orcamentoSearchTerm"
          data-testid="wizard-orcamento-search"
          :placeholder="t('Busque pelo nome do cliente ou nº do orçamento')"
          elem-id="wizard_orcamento_search"
          style="margin-bottom: 10px; width: 100%"
        />

        <!-- Loading Spinner -->
        <div v-if="loadingOrcamentos" class="loading-container">
          <i class="pi pi-spin pi-spinner" style="font-size: 3rem;"></i>
        </div>

        <!-- Orcamentos List -->
        <div class="orcamentos-list" v-else>
          <div
            v-for="orcamento in filteredOrcamentos"
            :key="orcamento.orcamento_id"
            :data-testid="`wizard-orcamento-${orcamento.orcamento_id}-btn`"
            class="orcamento-item"
            :style="{ backgroundColor: orcamento.background_color || '#fff' }"
            @click="selectOrcamentoAndStartWizard(orcamento)">
            <Button
              :data-testid="`wizard-orcamento-${orcamento.orcamento_id}-editar-btn`"
              icon="pi pi-pencil"
              :label="t('Editar')"
              text
              size="small"
              class="edit-button"
              @click.stop="editOrcamento(orcamento.orcamento_id)"
            />
            <div class="orcamento-info">
              <div class="client-name">
                <i class="pi pi-user"></i>
                <b><span class="text-capitalize">{{ orcamento.orcamento_cliente_nome || orcamento.cliente_nome }}</span></b>
              </div>
              <div v-if="getFranchiseName(orcamento.orcamento_franquia_id)" class="franchise-info">
                <i class="pi pi-building"></i>
                <span class="text-capitalize">{{ getFranchiseName(orcamento.orcamento_franquia_id) }}</span>
              </div>
              <div class="location-info">
                <i class="pi pi-map-marker"></i>
                <span class="text-capitalize">{{ orcamento.localidade_nome }} - {{ orcamento.orcamento_bairro }}</span>
              </div>
              <div class="address-info">
                <span class="text-capitalize">{{ orcamento.orcamento_rua }}, {{ orcamento.orcamento_numero }}</span>
              </div>
              <div><b>{{ t('Valor') }}:</b> <span style="white-space: nowrap">{{ n(parseFloat(orcamento.orcamento_valor ?? '0') || 0, 'currency') }}</span></div>
              <div><b>{{ t('Serviços concluídos') }}/{{ t('Total') }}:</b> {{ orcamento.servicos_concluidos }}/{{ orcamento.servicos_total }}</div>
            </div>
          </div>
        </div>

        <Message v-if="!loadingOrcamentos && filteredOrcamentos.length === 0" severity="info">
          <template #icon>
            <i class="pi pi-info-circle"></i>
          </template>
          {{ t('Nenhum orçamento disponível') }}
        </Message>

        <!-- Os orçamentos da busca que ainda não podem ir para a agenda, travados e com o motivo
             (chamado DS 2026-0068): ninguém fica sem saber por que o orçamento não está na lista. -->
        <p v-if="!orcamentoSearchTerm.trim()" class="fora-da-agenda-dica" data-testid="wizard-fora-da-agenda-dica">
          <i class="pi pi-question-circle"></i>
          {{ t('Não achou o orçamento? Busque pelo cliente ou pelo número: mostramos por que ele ainda não pode ir para a agenda.') }}
        </p>
        <div v-else-if="travados.length || foraDaAgenda.buscando" class="fora-da-agenda" data-testid="wizard-fora-da-agenda">
          <h5>{{ t('Ainda não podem ir para a agenda') }} <i v-if="foraDaAgenda.buscando" class="pi pi-spin pi-spinner"></i></h5>
          <div
            v-for="o in travados"
            :key="o.orcamento_id"
            :data-testid="`wizard-fora-da-agenda-${o.orcamento_id}`"
            class="fora-da-agenda-item"
          >
            <div><i class="pi pi-lock"></i> <b>#{{ o.orcamento_id }}</b> {{ o.cliente_nome }} — {{ o.orcamento_status }}</div>
            <ul>
              <li v-for="m in o.motivos" :key="m">{{ motivoTexto(m) }}</li>
            </ul>
            <div class="fora-da-agenda-acoes">
              <Button
                v-if="podeAprovarAqui(o.motivos)"
                :data-testid="`wizard-fora-da-agenda-${o.orcamento_id}-aprovar-btn`"
                :label="t('Aprovar')"
                icon="pi pi-check"
                size="small"
                severity="success"
                @click="aprovarForaDaAgenda(o.orcamento_id)"
              />
              <Button
                v-if="faltaCadastroDoCliente(o.motivos) && o.cliente_id"
                :data-testid="`wizard-fora-da-agenda-${o.orcamento_id}-cliente-btn`"
                :label="t('Completar cadastro do cliente')"
                icon="pi pi-user-edit"
                size="small"
                outlined
                @click="completarCadastroDoCliente(o.cliente_id)"
              />
              <Button
                v-if="(o.motivos ?? []).includes('sem_servico_pendente')"
                :data-testid="`wizard-fora-da-agenda-${o.orcamento_id}-servico-btn`"
                :label="t('Adicionar serviço')"
                icon="pi pi-plus"
                size="small"
                outlined
                @click="editOrcamento(o.orcamento_id)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 0: Service Selection -->
    <template #step-0>
      <div class="step-content">
        <h3 style="word-wrap: break-word">{{ t('Selecione os serviços que serão realizados nesse compromisso') }}:</h3>
        <Divider />

        <div v-if="loadingServices" class="loading-container">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
          <p>{{ t('Carregando serviços...') }}</p>
        </div>

        <Message v-else-if="availableServices.length === 0" severity="warn">
          <template #icon>
            <i class="pi pi-exclamation-triangle"></i>
          </template>
          {{ t('Nenhum serviço disponível neste orçamento') }}
        </Message>

        <div v-else class="services-list">
          <div
            v-for="service in availableServices"
            :key="service.orcamento_servico_id"
            :data-testid="`wizard-service-${service.orcamento_servico_id}`"
            class="service-item"
            :class="{ 'service-selected': service.selected, 'service-indisponivel': !!motivoIndisponivel(service) }"
            @click="toggleService(service)">
            <i
              class="pi"
              :class="motivoIndisponivel(service) ? 'pi-ban' : (service.selected ? 'pi-check-square' : 'pi-square')"
              style="margin-right: 8px;"
            ></i>
            {{ service.servico_nome }} - {{ t('Qtd') }} {{ service.orcamento_servico_qtd || 1 }} - {{ n(parseFloat(String(service.servico_valor || service.orcamento_servico_valor || 0)), 'currency') }}
            <span
              v-if="motivoIndisponivel(service)"
              class="service-motivo"
              :data-testid="`wizard-service-${service.orcamento_servico_id}-motivo`"
            >{{ motivoIndisponivel(service) }}</span>
          </div>
        </div>

        <div class="total-container">
          <h4>{{ t('Total') }}: {{ n(selectedServicesTotal || 0, 'currency') }}</h4>
        </div>
      </div>
    </template>

    <!-- Step 1: Payment Method -->
    <template #step-1>
      <div class="step-content">
        <div>
          <h3 style="word-wrap: break-word">{{ t('Selecione a forma de pagamento que será utilizada') }}:</h3>
          <buttons-list
            :itens-select="paymentMethodOptions"
            :value="selectedPaymentMethod ?? undefined"
            @input="selectedPaymentMethod = $event"
            elem-id="wizard_payment">
          </buttons-list>
        </div>
      </div>
    </template>

    <!-- Step 2: Operator Selection -->
    <template #step-2>
      <div class="step-content">
        <div>
          <h3 style="word-wrap: break-word">{{ t('Selecione o operador que irá realizar o serviço') }}:</h3>
          <buttons-list
            :itens-select="operadorOptions"
            :value="selectedOperador ?? undefined"
            :search-enabled="true"
            @input="selectedOperador = $event"
            elem-id="wizard_operador">
          </buttons-list>
        </div>

        <!-- Bottom Warning Box -->
        <Box type="warning" :title="t('Informações Importantes')" style="margin-top: 24px;">
          <div class="warning-content">
            <div class="warning-item">
              <i class="pi pi-info-circle"></i>
              <span>{{ t('Verifique todas as informações antes de confirmar o compromisso') }}</span>
            </div>
            <div class="warning-item">
              <i class="pi pi-clock"></i>
              <span>{{ t('O horário selecionado será reservado na agenda do operador') }}</span>
            </div>
            <div class="warning-item">
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ t('Certifique-se de que o cliente está ciente do agendamento') }}</span>
            </div>
          </div>
        </Box>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useAgenda } from '../../composables/useAgenda';
import { useOrcamento } from '../../composables/useOrcamento';
import { useAgendarOrcamento } from '../../composables/useAgendarOrcamento';
import type { Orcamentos_BuscarOrcamentosParaCompromissoRow } from '../../contracts/Orcamentos_BuscarOrcamentosParaCompromissoRow';
import { useFiltro } from '../../composables/useFiltro';
import { permissionsService } from '../../services/permissionsService';
import { log } from '../../utils/DebugLogger';
import WizardModal from '../../ui/WizardModal.vue';
import ButtonsList from '../../inputs/ButtonsList.vue';
import type { Agenda_AgendarCompromissoInput } from '../../contracts/Agenda_AgendarCompromissoInput';
import '../../contracts/Agenda_AgendarCompromissoInput';

type Id = number | string;

interface OrcamentoItem {
  orcamento_id?: Id;
  orcamento_cliente_nome?: string;
  cliente_nome?: string;
  orcamento_franquia_id?: Id;
  orcamento_bairro?: string;
  orcamento_rua?: string;
  orcamento_numero?: string;
  orcamento_valor?: string;
  orcamento_origem?: string;
  localidade_nome?: string;
  servicos_pendentes?: number;
  servicos_concluidos?: number;
  servicos_total?: number;
  background_color?: string;
  [key: string]: unknown;
}

interface ServiceItem {
  orcamento_servico_id?: Id;
  /** 1 criado · 2 aprovado · 3 agendado · 4 concluído · 5 removido (ver motivoIndisponivel) */
  orcamento_servico_status?: number;
  servico_nome?: string;
  servico_valor?: string | number;
  orcamento_servico_valor?: string | number;
  orcamento_servico_total?: string | number;
  orcamento_servico_qtd?: number | null;
  selected?: boolean;
  [key: string]: unknown;
}

interface SelectOption {
  ID: Id;
  Name: string;
  [key: string]: unknown;
}

interface SummaryItem {
  icon: string;
  label: string;
  value: string;
}

interface WizardOpenPayload {
  start?: string | Date | null;
  end?: string | Date | null;
  [key: string]: unknown;
}

const emit = defineEmits<{ 'close': [] }>();

const { t, n } = useI18n();
const { buscarOrcamentos, buscarFormasPagamento, agendarCompromisso } = useAgenda();
const { buscarOrcamento, buscarOrcamentosForaDaAgenda, aprovarOrcamento } = useOrcamento();
const { motivoTexto, faltaCadastroDoCliente } = useAgendarOrcamento();
const { getSelectedFranchisesSelectArray } = useFiltro();

// Modal state
const isVisible = ref<boolean>(false);
const showingOrcamentoSelection = ref<boolean>(true);

// Wizard state
const currentStep = ref<number>(0);
const wizardType = ref<string | null>(null);

// Selected data
const selectedDateTime = ref<{ start: string | Date | null; end: string | Date | null }>({ start: null, end: null });
const selectedOrcamento = ref<OrcamentoItem | null>(null);
const availableServices = ref<ServiceItem[]>([]);
const loadingServices = ref<boolean>(false);
const selectedOperador = ref<Id | null>(null);
const selectedPaymentMethod = ref<Id | null>(null);

// Orcamentos list
const orcamentosAprovados = ref<OrcamentoItem[]>([]);
const loadingOrcamentos = ref<boolean>(false);
const orcamentoSearchTerm = ref<string>('');

// Franchises list for name lookup
const franchisesArray = ref<SelectOption[]>([]);

// Options
const operadorOptions = ref<SelectOption[]>([]);
const paymentMethodOptions = ref<SelectOption[]>([]);

// Wizard steps definition with validation
const wizardSteps = computed(() => [
  { title: t('Serviços'), validate: (): boolean => selectedServicesCount.value > 0 },
  { title: t('Pagamento'), validate: (): boolean => !!selectedPaymentMethod.value },
  { title: t('Operador'), validate: (): boolean => !!selectedOperador.value }
]);

// Summary items for built-in summary template
const summaryItems = computed((): SummaryItem[] => {
  const items: SummaryItem[] = [];
  if (selectedDateTime.value?.start) {
    let dateTimeValue = formatDateTime(selectedDateTime.value.start);
    if (selectedDateTime.value.end) {
      dateTimeValue += ` - ${formatTime(selectedDateTime.value.end)}`;
    }
    items.push({ icon: 'pi pi-calendar', label: t('Data'), value: dateTimeValue });
  }
  if (selectedOrcamento.value) {
    const clienteName = selectedOrcamento.value.orcamento_cliente_nome || selectedOrcamento.value.cliente_nome;
    if (clienteName) items.push({ icon: 'pi pi-user', label: t('Cliente'), value: clienteName });
    const franchiseName = getFranchiseName(selectedOrcamento.value.orcamento_franquia_id);
    if (franchiseName) items.push({ icon: 'pi pi-building', label: t('Franquia'), value: franchiseName });
  }
  if (selectedServicesCount.value > 0) {
    items.push({ icon: 'pi pi-shopping-cart', label: t('Total'), value: n(Number(selectedServicesTotal.value) || 0, 'currency') });
  }
  if (selectedPaymentMethodName.value) {
    items.push({ icon: 'pi pi-credit-card', label: t('Pagamento'), value: selectedPaymentMethodName.value });
  }
  return items;
});

const filteredOrcamentos = computed((): OrcamentoItem[] => {
  if (!orcamentoSearchTerm.value) {
    return orcamentosAprovados.value.filter((o) => (o.servicos_pendentes ?? 0) > 0);
  }
  const term = orcamentoSearchTerm.value.trim().toLowerCase();
  return orcamentosAprovados.value.filter((o) => {
    const clienteNome = (o.orcamento_cliente_nome || o.cliente_nome || '').toLowerCase();
    return (o.servicos_pendentes ?? 0) > 0 && (
      String(o.orcamento_id ?? '') === term ||
      clienteNome.includes(term) ||
      o.orcamento_bairro?.toLowerCase().includes(term) ||
      o.localidade_nome?.toLowerCase().includes(term)
    );
  });
});

const selectedServicesCount = computed((): number =>
  availableServices.value.filter((s) => s.selected).length
);

const selectedServicesTotal = computed((): number =>
  availableServices.value
    .filter((s) => s.selected)
    .reduce((sum, s) => sum + parseFloat(String(s.servico_valor || s.orcamento_servico_valor || s.orcamento_servico_total || 0)), 0)
);

const selectedPaymentMethodName = computed((): string => {
  if (!selectedPaymentMethod.value) return '';
  const method = paymentMethodOptions.value.find((o) => o.ID === selectedPaymentMethod.value);
  return method ? String(method.Name) : '';
});

const nextButtonLabel = computed(() => (step: number): string => {
  if (step === 2) return t('Adicionar à agenda');
  return t('Próximo');
});

const nextButtonIcon = computed(() => (step: number): string => {
  if (step === 2) return 'pi pi-check';
  return 'pi pi-arrow-right';
});

// Methods
const formatDateTime = (date: string | Date | null): string => {
  if (!date) return '';
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatDateOnly = (date: string | Date | null): string => {
  if (!date) return '';
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatTime = (date: string | Date | null): string => {
  if (!date) return '';
  const d = new Date(date);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const getFranchiseName = (franchiseId: Id | undefined): string => {
  if (!franchiseId || !franchisesArray.value.length) return '';
  const franchise = franchisesArray.value.find((f) => f.ID === franchiseId) ||
    franchisesArray.value.find((f) => String(f.ID) === String(franchiseId));
  return franchise ? String(franchise.Name) : '';
};

const formatDateTimeForBackend = (date: string | Date | null): string => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const loadFranchises = async (): Promise<void> => {
  try {
    franchisesArray.value = await getSelectedFranchisesSelectArray() as SelectOption[];
  } catch (error) {
    log('AgendaAddCompromissoWizard', 'loadFranchises', 'Error:', error);
    franchisesArray.value = [];
  }
};

const loadOrcamentos = async (): Promise<void> => {
  loadingOrcamentos.value = true;
  try {
    log('AgendaAddCompromissoWizard', 'loadOrcamentos', 'Loading orcamentos');
    const franquias = permissionsService.getFranquias() as Record<string, unknown>;
    const franquiasIds = Object.keys(franquias);
    const response = await buscarOrcamentos(franquiasIds) as { success: boolean; dados?: { orcamentos?: OrcamentoItem[] } };
    if (response.success && response.dados?.orcamentos) {
      orcamentosAprovados.value = response.dados.orcamentos;
    }
  } catch (error) {
    log('AgendaAddCompromissoWizard', 'loadOrcamentos', 'Error:', error);
  } finally {
    loadingOrcamentos.value = false;
    if (isVisible.value) eventBus.emit('agenda.wizard.ready');
  }
};

const loadPaymentMethods = async (): Promise<void> => {
  try {
    const response = await buscarFormasPagamento() as { success: boolean; dados?: { formas_pagamento?: Array<{ forma_pagamento_id: Id; forma_pagamento_nome: string }> } };
    if (response.success && response.dados?.formas_pagamento) {
      paymentMethodOptions.value = response.dados.formas_pagamento.map((fp) => ({
        ID: fp.forma_pagamento_id,
        Name: fp.forma_pagamento_nome
      }));
    }
  } catch (error) {
    log('AgendaAddCompromissoWizard', 'loadPaymentMethods', 'Error:', error);
    paymentMethodOptions.value = [];
  }
};

const loadOperadores = (franquiaId: Id | undefined): void => {
  try {
    if (!franquiaId) { operadorOptions.value = []; return; }
    operadorOptions.value = permissionsService.getOperadoresSelectArrayByFranquiaID(Number(franquiaId)) as SelectOption[];
  } catch (error) {
    log('AgendaAddCompromissoWizard', 'loadOperadores', 'Error:', error);
    operadorOptions.value = [];
  }
};

const open = (payload: WizardOpenPayload): void => {
  log('AgendaAddCompromissoWizard', 'open', 'Opening wizard', payload);
  currentStep.value = 0;
  wizardType.value = null;
  showingOrcamentoSelection.value = true;
  selectedDateTime.value = { start: payload.start || null, end: payload.end || null };
  selectedOrcamento.value = null;
  availableServices.value = [];
  selectedOperador.value = null;
  selectedPaymentMethod.value = null;
  orcamentoSearchTerm.value = '';
  foraDaAgenda.value = { buscando: false, resultados: [] };
  loadFranchises();
  loadPaymentMethods();
  loadOrcamentos();
  isVisible.value = true;
};

const handleModalClose = (): void => {
  if (showingOrcamentoSelection.value || currentStep.value === 0) {
    showingOrcamentoSelection.value = true;
    currentStep.value = 0;
  }
};

const handleCancel = (): void => {
  if (!showingOrcamentoSelection.value && currentStep.value === 0) {
    showingOrcamentoSelection.value = true;
    currentStep.value = 0;
  } else {
    handleClose();
  }
};

const handleClose = (): void => {
  isVisible.value = false;
  showingOrcamentoSelection.value = true;
  currentStep.value = 0;
  wizardType.value = null;
  emit('close');
};

const selectOrcamentoAndStartWizard = async (orcamento: OrcamentoItem): Promise<void> => {
  selectedOrcamento.value = orcamento;
  wizardType.value = 'orcamento';
  loadOperadores(orcamento.orcamento_franquia_id);
  await loadOrcamentoServices();
  showingOrcamentoSelection.value = false;
  currentStep.value = 0;
  eventBus.emit('agenda.wizard.step.ready', { step: 0 });
};

/**
 * Os status de `orcamento_servico_status` que ainda PODEM ser agendados.
 *
 * A máquina inteira, cada estado com quem o escreve no app-api:
 *   1 criado      orcamentos.php, ao criar o serviço no orçamento
 *   2 aprovado    aprovar orçamento — e deletar um compromisso DEVOLVE para 2
 *   3 agendado    atendente_interactor.php, dentro do próprio agendar_compromisso
 *   4 concluído   compromissos_model.php, ao finalizar o compromisso
 *   5 removido    wizard_servicos.php
 *
 * Chamado 2026-0040: esta tela listava TODOS e ainda marcava todos, então o
 * serviço já agendado (3) e o já realizado (4) voltavam pré-selecionados e eram
 * reagendados sem ninguém notar — "o serviço de limpeza já estava na agenda e
 * puxou novamente". Medido na base: 125 de 157 linhas estavam em 4.
 *
 * O 5 é o único que SOME: ele foi removido do orçamento, não faz mais parte
 * dele, e o próprio `servicos_total` do app-api já o exclui da conta. O 3 e o 4
 * aparecem travados, com o motivo ao lado.
 */
const STATUS_REMOVIDO = 5;

/**
 * Por que este serviço não pode ser agendado — ou '' quando pode.
 *
 * Mostrar travado, e não esconder: o que o operador precisa saber é que o
 * serviço EXISTE e já tem destino. Sumir da lista responde "não pode agendar"
 * mas cala sobre "por quê", e a Léia teria de abrir a agenda para descobrir se
 * o serviço foi feito, está marcado para outro dia, ou sumiu por defeito.
 */
const motivoIndisponivel = (service: ServiceItem): string => {
  const status = Number(service.orcamento_servico_status);
  if (status === 3) return t('já agendado');
  if (status === 4) return t('já realizado');
  return '';
};

const loadOrcamentoServices = async (): Promise<void> => {
  if (!selectedOrcamento.value) return;
  loadingServices.value = true;
  try {
    const response = await buscarOrcamento(Number(selectedOrcamento.value.orcamento_id)) as { success: boolean; dados?: { servicos?: ServiceItem[] } };
    if (response.success && response.dados?.servicos) {
      // A decisão de quem pode ser agendado é AQUI e não no backend de propósito:
      // `orcamentos/buscar_orcamento` também alimenta o OrcamentosModal, que
      // PRECISA mostrar o orçamento inteiro. O status já vem no contrato
      // (Agenda_BuscarCompromissoOrcamentoServicoRow); faltava olhar para ele.
      availableServices.value = response.dados.servicos
        .filter((s) => Number(s.orcamento_servico_status) !== STATUS_REMOVIDO)
        // Nada pré-marcado: agendar é uma escolha, não o default. Com tudo
        // marcado, a ação mais fácil do operador era justamente a errada.
        .map((s) => ({ ...s, selected: false }));
    }
  } catch (error) {
    log('AgendaAddCompromissoWizard', 'loadOrcamentoServices', 'Error:', error);
  } finally {
    loadingServices.value = false;
  }
};

const toggleService = (service: ServiceItem): void => {
  // O travado não alterna. A lista mostra o serviço para a pessoa SABER que ele
  // existe e já tem destino, não para ela poder agendá-lo de novo.
  if (motivoIndisponivel(service)) return;
  service.selected = !service.selected;
};

const selectBloqueio = (): void => {
  handleClose();
  eventBus.emit('bloqueio-agenda.open', { dataInicio: selectedDateTime.value.start });
};

// Os orçamentos da busca que não podem ir para a agenda, com o motivo (chamado DS 2026-0068).
// A mesma busca do topo consulta o app-api (cliente ou número), 400 ms depois da última tecla.
const foraDaAgenda = ref<{ buscando: boolean; resultados: Orcamentos_BuscarOrcamentosParaCompromissoRow[] }>({ buscando: false, resultados: [] });
/** Só os travados: o que pode ir para a agenda já está na lista de cima. */
const travados = computed(() => foraDaAgenda.value.resultados.filter((o) => (o.motivos ?? []).length > 0));

const buscarForaDaAgenda = async (): Promise<void> => {
  const termo = orcamentoSearchTerm.value.trim();
  if (termo.length < 2) {
    foraDaAgenda.value.resultados = [];
    return;
  }
  foraDaAgenda.value.buscando = true;
  try {
    const resultados = await buscarOrcamentosForaDaAgenda(termo);
    // A pessoa continuou digitando: esta resposta é de uma busca velha.
    if (termo === orcamentoSearchTerm.value.trim()) foraDaAgenda.value.resultados = resultados;
  } catch (error) {
    log('AgendaAddCompromissoWizard', 'buscarForaDaAgenda', 'Error:', error);
    eventBus.emit('globalMessage.error', error instanceof Error ? error.message : t('Erro ao buscar orçamentos'));
  } finally {
    foraDaAgenda.value.buscando = false;
  }
};

let esperaDaBusca: ReturnType<typeof setTimeout> | undefined;
watch(orcamentoSearchTerm, () => {
  clearTimeout(esperaDaBusca);
  esperaDaBusca = setTimeout(() => { void buscarForaDaAgenda(); }, 400);
});

/** Aprovar aqui só resolve quando falta SÓ a aprovação. */
const podeAprovarAqui = (motivos: string[] | undefined): boolean =>
  (motivos ?? []).length === 1 && (motivos ?? [])[0] === 'nao_aprovado';

const aprovarForaDaAgenda = async (orcamentoId: number): Promise<void> => {
  try {
    await aprovarOrcamento(orcamentoId);
    eventBus.emit('globalMessage.success', t('Orçamento aprovado com sucesso'));
    await loadOrcamentos();
    await buscarForaDaAgenda();
  } catch (error) {
    eventBus.emit('globalMessage.error', error instanceof Error ? error.message : t('Erro ao aprovar orçamento'));
  }
};

const completarCadastroDoCliente = (clienteId: number): void => {
  handleClose();
  eventBus.emit('cliente.edit', clienteId);
};

const editOrcamento = (orcamentoId: Id | undefined): void => {
  handleClose();
  eventBus.emit('orcamento.edit', orcamentoId);
};

const createCompromisso = async (): Promise<void> => {
  try {
    const selectedServices = availableServices.value
      .filter((s) => s.selected)
      .map((s) => ({
        orcamento_servico_id: Number(s.orcamento_servico_id),
        servico_valor: Number(s.servico_valor || s.orcamento_servico_valor || 0),
        orcamento_servico_qtd: Number(s.orcamento_servico_qtd) || 1
      }));

    const compromissoData: Agenda_AgendarCompromissoInput = {
      title: selectedOrcamento.value?.orcamento_bairro ?? '',
      compromisso_total: selectedServicesTotal.value,
      cor_fundo: '#A6851B',
      compromisso_orcamento_id: selectedOrcamento.value?.orcamento_id !== undefined ? Number(selectedOrcamento.value.orcamento_id) : undefined,
      compromisso_franquia_id: Number(selectedOrcamento.value?.orcamento_franquia_id),
      compromisso_operador_user_id: String(selectedOperador.value ?? ''),
      compromisso_data_hora_inicio: formatDateTimeForBackend(selectedDateTime.value.start),
      compromisso_data_hora_fim: formatDateTimeForBackend(selectedDateTime.value.end),
      compromisso_forma_pagamento: selectedPaymentMethod.value !== null ? Number(selectedPaymentMethod.value) : undefined,
      compromisso_numero_parcelas: 1,
      compromisso_tipo: 1,
      servicos: selectedServices,
      ...(selectedOrcamento.value?.orcamento_origem
        ? { compromisso_origem: Number(selectedOrcamento.value.orcamento_origem) }
        : {})
    };

    const response = await agendarCompromisso(compromissoData) as { success: boolean; dados?: { compromisso_id?: Id }; message?: string };
    if (response.success) {
      handleClose();
      eventBus.emit('compromisso.created', { compromisso_id: response.dados?.compromisso_id });
      eventBus.emit('globalMessage.show', { type: 'success', message: t('Compromisso criado com sucesso!') });
    } else {
      throw new Error(response.message || 'Erro ao criar compromisso');
    }
  } catch (error) {
    log('AgendaAddCompromissoWizard', 'createCompromisso', 'Error:', error);
    alert(t('Erro ao criar compromisso: ') + (error as Error).message);
  }
};

// Event listeners
onMounted(() => {
  log('AgendaAddCompromissoWizard', 'onMounted', 'Component mounted');
  eventBus.on('agenda-add-compromisso.open', open as (data?: unknown) => void);
});

onBeforeUnmount(() => {
  log('AgendaAddCompromissoWizard', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('agenda-add-compromisso.open', open as (data?: unknown) => void);
  clearTimeout(esperaDaBusca);
});

defineExpose({ open, close: handleClose });
</script>

<style scoped>
/* Os orçamentos da busca que ainda não podem ir para a agenda */
.fora-da-agenda {
  margin-top: 12px;
}
.fora-da-agenda h5 {
  margin: 0 0 8px;
}
.fora-da-agenda-dica {
  margin-top: 12px;
  color: #5b6168;
  font-size: 13px;
}
.fora-da-agenda-item {
  border: 1px dashed var(--p-content-border-color, #ccc);
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: #f4f3ef;
  color: #5b6168;
}
.fora-da-agenda-item ul {
  margin: 4px 0;
  padding-left: 18px;
}
.fora-da-agenda-acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Bloqueio Button */
.bloqueio-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 15px;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  gap: 10px;
}

/* Orcamentos List */
.orcamentos-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.orcamento-item {
  position: relative;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.orcamento-item:hover {
  opacity: 0.9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.orcamento-item .edit-button {
  position: absolute;
  top: 10px;
  right: 10px;
}

.orcamento-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.client-name,
.franchise-info,
.location-info,
.address-info {
  display: flex;
  align-items: center;
  gap: 5px;
}

.client-name {
  margin-bottom: 8px;
}

.address-info {
  margin-left: 20px;
}

/* Services List */
.services-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 20px;
}

.service-item {
  padding: 12px 15px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.service-item:hover {
  background-color: #f5f5f5;
}

.service-selected {
  background-color: #fcf8e3;
  border-color: #faebcc;
  color: #8a6d3b;
}

/* Serviço que já tem destino (agendado ou realizado): visível, mas fora de
   alcance — chamado 2026-0040. O `not-allowed` e o esmaecido dizem, antes do
   clique, que ali não há o que fazer. */
.service-indisponivel {
  background-color: #f7f7f7;
  border-color: #e5e5e5;
  color: #9a9a9a;
  cursor: not-allowed;
}

.service-indisponivel:hover {
  background-color: #f7f7f7;
}

.service-motivo {
  margin-left: auto;
  padding-left: 12px;
  font-size: 0.85em;
  font-style: italic;
  white-space: nowrap;
}

/* Total Container */
.total-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* Step Content */
.step-content {
  padding: 20px 20px;
  min-height: 400px;
  border-radius: 8px !important;
}

/* Fix text overflow in buttons-list labels */
:deep(.buttons-list),
:deep(.buttons-list *),
:deep(.buttons-list-label),
:deep(.buttons-list .label),
:deep(.buttons-list label),
:deep(.buttons-list .form-label),
:deep(.buttons-list .control-label),
:deep(.buttons-list .col-form-label),
:deep(.buttons-list .form-control-label),
:deep(.step-content .buttons-list),
:deep(.step-content .buttons-list *),
:deep(.step-content .buttons-list label),
:deep(.step-content label) {
  word-wrap: break-word !important;
  word-break: break-word !important;
  white-space: normal !important;
  line-height: 1.4 !important;
  hyphens: auto !important;
  max-width: 100% !important;
  overflow-wrap: break-word !important;
  overflow: visible !important;
}

:deep(.buttons-list) {
  width: 100%;
  overflow-wrap: break-word;
  max-width: 100%;
  overflow: visible;
}

:deep(.step-content) {
  overflow-wrap: break-word;
  word-break: break-word;
}

/* Text Utilities */
.text-capitalize {
  text-transform: capitalize;
}

/* Warning Box Content */
.warning-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.warning-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
}

.warning-item i {
  font-size: 1.125rem;
  color: var(--app-warning-color);
  margin-top: 2px;
  flex-shrink: 0;
}

.warning-item span {
  font-size: 0.875rem;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.87);
}

/* Responsive Design */
/* No custom responsive styles needed - handled by WizardModal */
</style>
